import { useQuery } from '@tanstack/react-query';
import useDebounce from '@elementary/common-ui/hooks/useDebounce';
import { TICKET_SEARCH } from '@elementary/common-ui/constants/queryKeys';
import { searchTicketingIssues } from 'shared/services/settingsApi';

const TICKET_SEARCH_DEBOUNCE_MS = 300;

const JIRA_BROWSE_URL_PATTERN = /\/browse\/([A-Za-z]+-\d+)/i;
const LINEAR_ISSUE_URL_PATTERN = /\/issue\/([A-Za-z]+-\d+)/i;

/**
 * Given a raw user input (which may be a full ticket URL), returns just the
 * ticket key. Handles Jira (/browse/KEY-123) and Linear (/issue/KEY-123/).
 * Falls back to the original input (unmodified) if no URL pattern matches,
 * so callers can use this on every keystroke without stripping whitespace
 * the user is actively typing.
 */
export const extractTicketKey = (input: string): string => {
  const jiraMatch = input.match(JIRA_BROWSE_URL_PATTERN);
  if (jiraMatch) return jiraMatch[1].toUpperCase();
  const linearMatch = input.match(LINEAR_ISSUE_URL_PATTERN);
  if (linearMatch) return linearMatch[1].toUpperCase();
  return input;
};

export type TicketSelection = {
  key: string;
  summary: string;
  url: string;
};

type Params = {
  envId: string;
  integrationId: string;
  query: string;
  enabled?: boolean;
};

const useTicketSearch = ({
  envId,
  integrationId,
  query,
  enabled = true,
}: Params) => {
  const debouncedQuery = useDebounce(query, TICKET_SEARCH_DEBOUNCE_MS);
  const normalizedQuery = debouncedQuery.trim().toLowerCase();
  const { data, isFetching, isError } = useQuery({
    queryKey: [TICKET_SEARCH, envId, integrationId, normalizedQuery],
    queryFn: () => searchTicketingIssues(envId, integrationId, normalizedQuery),
    enabled: enabled && !!envId && !!integrationId,
    select: (response): TicketSelection[] =>
      response.results.map((result) => ({
        key: result.key,
        summary: result.summary,
        url: result.url,
      })),
    retry: false,
  });

  return {
    results: data,
    isSearching: isFetching,
    isError,
    debouncedQuery,
  };
};

export default useTicketSearch;
